import {
    $applyNodeReplacement,
    type DOMConversionMap,
    type DOMConversionOutput,
    type DOMExportOutput,
    type EditorConfig,
    type LexicalNode,
    type NodeKey,
    TextNode,
} from 'lexical';

import { SerializedMentionNode } from './types';

// Helper Functions
const $convertMentionElement = ({ textContent, getAttribute }: HTMLElement): DOMConversionOutput | null => {
    const mentionName = getAttribute('data-lexical-mention-name');

    if (textContent !== null) {
        const node = $createMentionNode(
            typeof mentionName === 'string' ? mentionName : textContent,
            '',
            textContent,
        );
        return { node };
    }

    return null;
};

// Node Class
export class MentionNode extends TextNode {
    __mention: string;

    __correlationId: string;

    // Static Methods
    static getType(): string {
        return 'mention';
    }

    static clone(node: MentionNode): MentionNode {
        return new MentionNode(node.__mention, node.__correlationId, node.__text, node.__key);
    }

    static importJSON(serializedNode: SerializedMentionNode): MentionNode {
        return $createMentionNode(serializedNode.mentionName, serializedNode.correlationId ?? '').updateFromJSON(serializedNode);
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-lexical-mention')) {
                    return null;
                }
                return {
                    conversion: $convertMentionElement,
                    priority: 1,
                };
            },
        };
    }

    // Constructor
    constructor(mentionName: string, correlationId: string, text?: string, key?: NodeKey) {
        super(text ?? mentionName, key);
        this.__mention = mentionName;
        this.__correlationId = correlationId;
    }

    getCorrelationId(): string {
        return this.getLatest().__correlationId;
    }

    // DOM Methods
    createDOM(config: EditorConfig): HTMLElement {
        const dom = super.createDOM(config);
        dom.className = 'NSRichTextEditor__mention';
        dom.spellcheck = false;
        return dom;
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span');
        element.setAttribute('data-lexical-mention', 'true');
        if (this.__text !== this.__mention) {
            element.setAttribute('data-lexical-mention-name', this.__mention);
        }
        element.textContent = this.__text;
        element.className = 'NSRichTextEditor__mention';
        return { element };
    }

    // Node Behavior Methods
    isTextEntity(): true {
        return true;
    }

    canInsertTextBefore(): boolean {
        return false;
    }

    canInsertTextAfter(): boolean {
        return false;
    }

    // Serialization Methods
    exportJSON(): SerializedMentionNode {
        return {
            ...super.exportJSON(),
            mentionName: this.__mention,
            correlationId: this.__correlationId,
        };
    }
}

// Utility Functions
export function $createMentionNode(
    mentionName: string,
    correlationId: string,
    textContent?: string,
): MentionNode {
    const mentionNode = new MentionNode(mentionName, correlationId, textContent ?? mentionName);
    mentionNode.setMode('segmented').toggleDirectionless();
    return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(
    node: LexicalNode | null | undefined,
): node is MentionNode {
    return node instanceof MentionNode;
}
